import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as marketApi from '../api/marketplace';
import { useOrg } from '../lib/org';
import { Spinner, ErrorAlert, EmptyState, Field, SelectField } from '../components/ui';
import VendorLogo from '../components/VendorLogo';
import MyInterestLists from '../components/MyInterestLists';
import MyRewards from '../components/MyRewards';
import { cn, ui } from '../lib/ui';

/**
 * /marketplace: the organization's directory of approved vendors
 * (docs/Decisions.md D67 as amended by D68), by category, with a search.
 */
export default function Marketplace() {
  const { activeOrg } = useOrg();
  const isOrgAdmin = activeOrg?.role === 'org-admin';
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState({ q: '', categoryId: '' });
  const [draft, setDraft] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await marketApi.getDirectory(query));
    } catch (err) {
      setError(err);
    }
  }, [query]);

  useEffect(() => {
    if (activeOrg) load();
  }, [load, activeOrg]);

  const title = (
    <div className={cn(ui.row, ui.spread, 'mb-2')}>
      <h1 className={cn(ui.h1, 'mb-0')}>Marketplace</h1>
      <div className={ui.row}>
        <Link className={cn(ui.btnSecondary, ui.btnSm)} to="/vendor">
          Vendor workspace
        </Link>
        {(isOrgAdmin || data?.isApprover) && (
          <Link className={cn(ui.btnSecondary, ui.btnSm)} to="/org/marketplace">
            Manage marketplace
          </Link>
        )}
      </div>
    </div>
  );

  if (!activeOrg) {
    return (
      <>
        {title}
        <EmptyState message="Choose an organization first." />
      </>
    );
  }

  const byId = new Map();
  for (const c of data?.categories || []) {
    byId.set(c.categoryId, c);
    for (const k of c.children) byId.set(k.categoryId, k);
  }

  return (
    <>
      {title}
      <p className={cn(ui.muted, 'mb-4')}>
        Services offered to members of {activeOrg.name} by vendors the organization approved.
        Members deal with vendors directly; the platform never takes payment for a service.
      </p>

      <form
        className={cn(ui.cardFlat, 'mb-6 grid gap-x-4 md:grid-cols-[2fr_1fr_auto] md:items-end')}
        role="search"
        onSubmit={e => {
          e.preventDefault();
          setQuery(prev => ({ ...prev, q: draft.trim() }));
        }}
      >
        <Field
          label="Search vendors and services"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          maxLength={100}
          placeholder="For example: bookkeeping, home insurance"
        />
        <SelectField
          label="Category"
          value={query.categoryId}
          onChange={e => setQuery(prev => ({ ...prev, categoryId: e.target.value }))}
        >
          <option value="">All categories</option>
          {(data?.categories || []).map(c => [
            <option key={c.categoryId} value={c.categoryId}>
              {c.name} ({c.vendorCount})
            </option>,
            ...c.children.map(k => (
              <option key={k.categoryId} value={k.categoryId}>
                {`- ${k.name} (${k.vendorCount})`}
              </option>
            )),
          ])}
        </SelectField>
        <button type="submit" className={cn(ui.btnPrimary, 'mb-4')}>
          Search
        </button>
      </form>

      {error ? (
        <ErrorAlert error={error} />
      ) : !data ? (
        <Spinner label="Loading the marketplace..." />
      ) : data.vendors.length === 0 ? (
        <EmptyState
          message={
            query.q || query.categoryId
              ? 'No vendor matches. Try another word or category.'
              : 'No vendors are listed yet. Vendors apply from the vendor workspace.'
          }
        />
      ) : (
        <ul className={cn(ui.grid, ui.gridMd2, 'm-0 list-none p-0')} aria-label="Vendors">
          {data.vendors.map(v => (
            <li key={v.vendorId} className={cn(ui.card, 'flex gap-4')}>
              <VendorLogo vendor={v} />
              <div className="min-w-0">
                <h2 className="mt-0 mb-1 text-title-s">
                  <Link to={`/marketplace/vendors/${encodeURIComponent(v.vendorId)}`}>
                    {v.businessName}
                  </Link>
                </h2>
                <p className={cn(ui.hint, 'mt-0 mb-2')}>
                  {v.categoryIds
                    .map(id => byId.get(id)?.name)
                    .filter(Boolean)
                    .join(', ')}
                  {v.serviceArea ? ` - ${v.serviceArea}` : ''}
                </p>
                <p className="m-0 line-clamp-3 text-body-s">{v.description}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
      <section aria-labelledby="my-lists-heading" className="mt-8">
        <h2 id="my-lists-heading" className={ui.h2}>
          Your interest lists
        </h2>
        <MyInterestLists />
      </section>
      <MyRewards />
    </>
  );
}
